import * as React from 'react';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ViewExploreIcon} from 'shells/icon';
import {BellRegularIcon} from 'shells/icon/svg/bell-regular-icon';
import {DripsRegularIcon} from 'shells/icon/svg/drips-regular-icon';
import {EyeRegularIcon} from 'shells/icon/svg/eye-regular-icon';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {TagRegularDiagonalIcon} from 'shells/icon/svg/tag-regular-diagonal-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Switch} from 'shells/switch/switch';
import {PaginatedTable} from 'shells/table/paginated-table';
import type {Column} from 'shells/table/table-types';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {ActionType} from 'nutshell-graphql-types';
import type {
    GetMoneyPages_maUrls_edges_node as MoneyPage,
    GetMoneyPages_maUrls_edges_node_automation as Automation,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {EmptyState} from './empty-state';
import {useDeleteMoneyPage} from './graphql/hooks/use-delete-money-page';
import {useSetMoneyPageIsEnabled} from './graphql/hooks/use-set-money-page-is-enabled';
import {PAGE_ACTIVITY_TAB_ID} from './money-page';

type Props = {
    moneyPages: MoneyPage[];
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
    totalCount: number;
    pageSize: number;
    isLoading: boolean;
    onPrimaryCtaClick: () => void;
    onSecondaryCtaClick: () => void;
};

export function MoneyPagesTable(props: Props) {
    const [deletingPageId, setDeletingPageId] = React.useState<string | null>(null);

    const {setMoneyPageIsEnabled} = useSetMoneyPageIsEnabled();
    const {deleteMoneyPage} = useDeleteMoneyPage();

    const columns: Column<any>[] = React.useMemo(() => {
        const cols: Column<any>[] = [
            {
                // @ts-expect-error
                id: 'name',
                Header: 'Name',
                accessor: (row: MoneyPage) => row,
                disableSortBy: true,
                minWidth: 75,
                width: 125,
                dataType: 'linkedName',
            },
            {
                // @ts-expect-error
                id: 'url',
                Header: 'URL',
                accessor: (row: MoneyPage) => {
                    return {
                        text: row.url.fullyQualifiedUrl,
                        description: row.url.pageTitle,
                    };
                },
                dataType: 'twoLineCell',
                minWidth: 200,
                width: 400,
                disableSortBy: true,
            },
            {
                // @ts-expect-error
                id: 'automations',
                Header: () => 'Automations',
                accessor: (row: MoneyPage) => row.automation,
                dataType: 'component',
                minWidth: 75,
                width: 110,
                disableSortBy: true,
                Cell: ({
                    cell,
                }: {
                    cell: {
                        value: Automation;
                    };
                }) => {
                    const automation = cell.value;
                    if (!automation) {
                        return null;
                    }
                    const automationIcons = getAutomationIcons(
                        automation.actions.map((action) => action.type),
                        automation.id
                    );

                    return (
                        <Stack isHorizontal={true} spacing={8}>
                            {automationIcons}
                        </Stack>
                    );
                },
            },
            {
                // @ts-expect-error
                id: 'lastTriggered',
                Header: 'Last triggered',
                dataType: 'component',
                disableSortBy: true,
                accessor: (row: MoneyPage) => row.lastTriggeredTime,
                minWidth: 100,
                width: 150,
                Cell: ({
                    cell,
                }: {
                    cell: {
                        value: number;
                    };
                }) => {
                    return <RelativeTimestamp datetime={cell.value} />;
                },
            },
            {
                // @ts-expect-error
                id: 'views',
                Header: 'Views',
                dataType: 'integer',
                disableSortBy: true,
                minWidth: 60,
                width: 100,
                accessor: (row: MoneyPage) => row.views,
            },
            {
                // @ts-expect-error
                id: 'isEnabled',
                Header: () => <span className='pl-8'>Enabled</span>,
                accessor: (row: MoneyPage) => row,
                dataType: 'boolean',
                disableSortBy: true,
                disableResizing: true,
                width: 80,
                Cell: ({
                    cell,
                }: {
                    cell: {
                        value: MoneyPage;
                    };
                }) => {
                    const {id, isEnabled} = cell.value;

                    return (
                        <div className='pl-8'>
                            <Switch
                                isOn={Boolean(isEnabled)}
                                isRounded={true}
                                isSmall={true}
                                shouldAnimate={true}
                                onChange={() => {
                                    setMoneyPageIsEnabled({
                                        id,
                                        isEnabled: !isEnabled,
                                    });
                                }}
                            />
                        </div>
                    );
                },
            },
            {
                // @ts-expect-error
                id: 'actions',
                accessor: (row: MoneyPage) => row,
                dataType: 'component',
                disableSortBy: true,
                disableResizing: true,
                width: 40,
                Cell: ({
                    cell,
                }: {
                    cell: {
                        value: MoneyPage;
                    };
                }) => {
                    const {htmlUrlPath} = cell.value;

                    return (
                        <ActionMenu
                            size='tiny'
                            options={[
                                {
                                    label: 'View activity',
                                    icon: <ViewExploreIcon size={16} wrapWithDiv={false} />,
                                    handleSelect: () => {
                                        appHistory.push(`${htmlUrlPath}/${PAGE_ACTIVITY_TAB_ID}`);
                                    },
                                },
                                {
                                    label: 'Edit page',
                                    icon: <EyeRegularIcon size={16} wrapWithDiv={false} />,
                                    handleSelect: () => {
                                        appHistory.push(htmlUrlPath);
                                    },
                                },
                                {
                                    label: 'Delete page',
                                    icon: <TrashRegularIcon size={16} wrapWithDiv={false} />,
                                    handleSelect: () => {
                                        setDeletingPageId(cell.value.id);
                                    },
                                },
                            ]}
                        />
                    );
                },
            },
        ];

        return cols;
    }, [setMoneyPageIsEnabled]);

    return (
        <>
            <PaginatedTable
                columns={columns}
                data={props.moneyPages}
                fetchNextPage={props.fetchNextPage}
                fetchPreviousPage={props.fetchPreviousPage}
                pageSize={props.pageSize}
                totalCount={props.totalCount}
                updateUrlParamsOnSortChange={() => {}}
                fullWidth={true}
                isLoading={props.isLoading}
                hasHeader={props.totalCount > 0}
                emptyState={
                    <EmptyState
                        onPrimaryCtaClick={props.onPrimaryCtaClick}
                        onSecondaryCtaClick={props.onSecondaryCtaClick}
                    />
                }
                disableVirtualization={true}
            />
            {deletingPageId && (
                <ConfirmationDialog
                    title='Are you sure you want to delete this marketing automation?'
                    message='The automation cannot be restored after deletion.'
                    onCancel={() => setDeletingPageId(null)}
                    onConfirm={() => {
                        deleteMoneyPage({
                            id: deletingPageId,
                        });
                        setDeletingPageId(null);
                    }}
                    confirmText='Delete'
                    isConfirmDestructive={true}
                />
            )}
        </>
    );
}

const AUTOMATION_TYPE_TO_ICON_MAP: Partial<Record<ActionType, React.ComponentType<any>>> = {
    ADD_TAGS: TagRegularDiagonalIcon,
    SEND_NOTIFICATION: BellRegularIcon,
    ADD_TO_DRIP_SEQUENCE: DripsRegularIcon,
    CREATE_LEAD: LeadChevronRegularIcon,
};

function getAutomationIcons(
    automationTypes: Array<ActionType>,
    id: string
): Array<React.ReactElement> {
    // @ts-expect-error
    return automationTypes.map((type) => {
        const Icon = AUTOMATION_TYPE_TO_ICON_MAP[type];

        return Icon ? <Icon key={`${id}-${type}`} size={16} wrapWithDiv={false} /> : null;
    });
}
